<template>
  <div
    class="popup-banner border-tm-gray-300 flex gap-2.5 rounded-xl border-1 bg-white p-2.5"
    :class="[
      props.fullWidth ? 'w-full' : 'tabletLandscape:w-96 w-88 max-w-[calc(100vw-1.25rem)]',
      props.flat ? '' : 'shadow-help-card',
      { 'items-center': !props.headline },
    ]"
    data-cy="genericPopUpBanner"
  >
    <div>
      <div class="flex flex-col items-start rounded-lg p-2" :class="chipClass">
        <GenericIcon :icon="props.icon" icon-size="size-5" :icon-color="chipIconColor" />
      </div>
    </div>
    <div class="flex w-full flex-col gap-1">
      <span
        v-if="props.headline"
        data-cy="popUpBannerHeadline"
        class="text-tm-gray-900 text-sm font-bold"
        >{{ props.headline }}</span
      >
      <div class="flex flex-col gap-2 break-words">
        <span v-if="props.segments" class="text-tm-gray-900 text-sm">
          <template v-for="(segment, index) in props.segments" :key="index"
            ><a
              v-if="segment.href"
              :href="segment.href"
              :class="segment.class ?? 'text-tm-petrol-base cursor-pointer underline'"
              >{{ segment.text }}</a
            ><button
              v-else-if="segment.onClick"
              type="button"
              :class="segment.class ?? 'text-tm-petrol-base cursor-pointer underline'"
              @click="segment.onClick"
            >
              {{ segment.text }}</button
            ><span v-else :class="segment.class">{{ segment.text }}</span></template
          >
        </span>
        <span v-else-if="props.contentText" class="text-tm-gray-900 text-sm font-semibold">{{
          props.contentText
        }}</span>

        <span v-if="props.timestampLabel" class="text-tm-petrol-base text-xxs font-semibold">{{
          props.timestampLabel
        }}</span>

        <slot />

        <div v-if="props.buttons && props.buttons.length" class="flex gap-2.5">
          <GenericButton
            v-for="(button, index) in props.buttons"
            :key="index"
            :data-cy="button.dataCy"
            size="small"
            :color-variant="button.colorVariant"
            :text="button.text"
            :icon="button.icon"
            :loading="button.loading"
            @click="button.onClick?.()"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!props.hideClose"
      :class="{ 'self-start': props.headline }"
      class="relative flex flex-col items-start"
    >
      <GenericButton
        class="text-tm-gray-900"
        icon="icon-[mingcute--close-line]"
        size="small"
        :color-variant="ColorVariants.EXTRA"
        @click="emit('onClose')"
      />
      <!-- The ring is the timer: its animation ending is what closes the banner. -->
      <svg
        v-if="props.autoCloseMs"
        class="pointer-events-none absolute inset-0 size-8"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        data-cy="popUpBannerAutoCloseRing"
      >
        <path
          class="auto-close-ring stroke-tm-gray-600"
          :class="{ 'auto-close-ring--paused': documentHidden }"
          :d="RING_PATH"
          pathLength="100"
          stroke-width="1.5"
          :style="{ animationDuration: `${props.autoCloseMs}ms` }"
          @animationend="emit('onClose')"
        />
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts">
import GenericButton from '../GenericButton/GenericButton.vue';
import { ColorVariants } from '@therame/design-tokens';
import GenericIcon from '../GenericIcon/GenericIcon.vue';
import type { MingcuteIconName, IconColor } from '../GenericIcon/index.ts';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

export interface PopUpBannerSegment {
  text: string;
  class?: string;
  href?: string;
  onClick?: () => void;
}

export interface PopUpBannerButton {
  text: string;
  colorVariant: ColorVariants;
  icon?: MingcuteIconName;
  loading?: boolean;
  onClick?: () => void;
  /** Test hook, for callers whose button is a gesture a spec has to reach. */
  dataCy?: string;
}

export interface GenericPopUpBannerProps {
  /** Drives the icon-chip colour via the design-system `color-variant-*` utilities. */
  variant?: ColorVariants;
  contentText?: string;
  segments?: PopUpBannerSegment[];
  buttons?: PopUpBannerButton[];
  timestampLabel?: string;
  /** Volle Breite statt fester Karten-Breite (z.B. im Posteingang). */
  fullWidth?: boolean;
  /** Ohne Schatten darstellen (z.B. im Posteingang). */
  flat?: boolean;
  icon: MingcuteIconName;
  /** Escape hatch for callers that style the chip without a `variant` (e.g. notifications). */
  iconColor?: IconColor;
  /** Escape hatch for callers that style the chip without a `variant` (e.g. notifications). */
  iconContentBgColor?: string;
  headline?: string;
  /**
   * Drops the ✕. For callers where dismissing is not what the banner offers —
   * the Posteingang archives instead of closing (PRD #1453), and a ✕ there used
   * to destroy the notification. Default keeps the close button, so every other
   * caller is unaffected.
   */
  hideClose?: boolean;
  /**
   * Closes the banner on its own after this many ms. The ✕ gets a ring that
   * empties counter-clockwise from 12 o'clock over that time; the ring's
   * animation is the timer, so it pauses while the banner is hovered or has
   * focus, and while the tab is hidden, so it holds until the user is back. Needs the ✕ —
   * with `hideClose` there is no ring and so no auto-close.
   */
  autoCloseMs?: number;
}

const props = withDefaults(defineProps<GenericPopUpBannerProps>(), {
  variant: undefined,
  contentText: undefined,
  segments: undefined,
  buttons: undefined,
  timestampLabel: undefined,
  fullWidth: false,
  flat: false,
  icon: 'icon-[mingcute--alert-fill]',
  iconColor: 'text-tm-petrol-base',
  iconContentBgColor: 'bg-tm-petrol-base/20 ',
  headline: undefined,
  hideClose: false,
  autoCloseMs: undefined,
});

/**
 * The ✕'s rounded square (32px, rounded-lg), inset by half the stroke and drawn
 * clockwise from 12 o'clock. Shrinking the dash from its far end makes the gap
 * open at 12 o'clock and grow counter-clockwise.
 */
const RING_PATH =
  'M16 0.75 H24 A7.25 7.25 0 0 1 31.25 8 V24 A7.25 7.25 0 0 1 24 31.25 H8 A7.25 7.25 0 0 1 0.75 24 V8 A7.25 7.25 0 0 1 8 0.75 Z';

const emit = defineEmits<{
  (event: 'onClose'): void;
}>();

/**
 * CSS animations run on the document timeline, which keeps advancing in a
 * background tab — without this the ring would run out unseen and the banner
 * would be gone by the time the user is back.
 */
const documentHidden = ref(false);
const syncDocumentHidden = () => {
  documentHidden.value = document.visibilityState === 'hidden';
};
onMounted(() => {
  syncDocumentHidden();
  document.addEventListener('visibilitychange', syncDocumentHidden);
});
onBeforeUnmount(() => document.removeEventListener('visibilitychange', syncDocumentHidden));

// Literal class strings (not interpolated) so Tailwind's scanner emits the utilities.
const VARIANT_CHIP: Record<ColorVariants, string> = {
  [ColorVariants.MOST_IMPORTANT]: 'color-variant-most-important',
  [ColorVariants.IMPORTANT]: 'color-variant-important',
  [ColorVariants.DEFAULT]: 'color-variant-default',
  [ColorVariants.ACTIVE]: 'color-variant-active',
  [ColorVariants.INACTIVE]: 'color-variant-inactive',
  [ColorVariants.DESTRUCTIVE]: 'color-variant-destructive',
  [ColorVariants.CATASTROPHIC]: 'color-variant-catastrophic',
  [ColorVariants.ERROR]: 'color-variant-error',
  [ColorVariants.WARNING]: 'color-variant-warning',
  [ColorVariants.INFO]: 'color-variant-info',
  [ColorVariants.IMPORTANT_ALTERNATIVE]: 'color-variant-important-alternative',
  [ColorVariants.ALTERNATIVE]: 'color-variant-alternative',
  [ColorVariants.DISABLED]: 'color-variant-disabled',
  [ColorVariants.PREMIUM]: 'color-variant-premium',
  [ColorVariants.EXTRA]: 'color-variant-extra',
};

// With a variant, the chip's colour comes from the utility and the icon inherits it.
const chipClass = computed(() =>
  props.variant ? VARIANT_CHIP[props.variant] : props.iconContentBgColor,
);
const chipIconColor = computed<IconColor | undefined>(() =>
  props.variant ? undefined : props.iconColor,
);
</script>

<style scoped>
.auto-close-ring {
  stroke-dasharray: 100;
  animation-name: auto-close-ring;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

.popup-banner:hover .auto-close-ring,
.popup-banner:focus-within .auto-close-ring,
.auto-close-ring--paused {
  animation-play-state: paused;
}

@keyframes auto-close-ring {
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: 100;
  }
}
</style>
